import { Data } from "@singularsystems/neo-core";
import { injectable } from "inversify";
import { AppService, DomainTypes, Types } from "../DomainTypes";
import PortfolioType from "../Models/Catalogue/Generic/PortfolioType";
import Bank from "../Models/Catalogue/Generic/Bank";
import Country from "../Models/Catalogue/General/Country";
import LegalEntityType from "../Models/Catalogue/Classifications/LegalEntityType";
import Sector from "../Models/Catalogue/Reporting/Sector";
import DocumentType from "../Models/Catalogue/Generic/DocumentType";
import AssetClass from "../Models/Catalogue/Generic/AssetClass";
import IssuerType from "../Models/Catalogue/General/IssuerType";
import ShareType from "../Models/Catalogue/General/ShareType";
import BankAccountType from "../Models/Catalogue/Pipeline/BankAccountType";
import Title from "../Models/Catalogue/Generic/Title";
import IntraGroup from "../Models/Catalogue/Generic/IntraGroup";
import InternalRating from "../Models/Catalogue/Investments/InternalRating";
import NoteType from "../Models/Catalogue/Generic/NoteType";
import InterestRateType from "../Models/Rates/InterestRateType";
import DateCycleLookup from "../Models/Dividends/Lookups/DateCycleLookup";
import InterestRateCalculationType from "../Models/Rates/InterestRateCalculationType";
import Entity from "../Models/Catalogue/Investments/Entity";
import AssetType from "../Models/Catalogue/Classifications/AssetType";
import GLAccountType from "../Models/Catalogue/General/GLAccountType";
import CommissionTypeLookup from "../Models/Catalogue/Pipeline/Lookups/CommissionTypeLookup";
import SARSIDType from "../Models/Catalogue/General/SARSIDType";
import Pipeline from "../Models/Catalogue/Pipeline/Pipeline";
import LetterAuthorisationSignatory from "../Models/LetterAuthorisation/LetterAuthorisationSignatory";
import DwtExemption from "../Models/Catalogue/General/DwtExemption";
import NoticePeriod from "../Models/Catalogue/Investments/NoticePeriod";
import ContactType from "../Models/Catalogue/General/ContactType";
import FieldConfigValues from "../Models/Config/FieldConfig/FieldConfigValues";
import { ConfigValues } from "../Models/Config/TenantConfig";
import SARSNatureOfPerson from "../Models/Catalogue/General/SARSNatureOfPerson";
import InternalApprovalEntity from "../Models/Approvals/InternalApprovalEntity";
import ProductType from "../Models/Catalogue/General/ProductType";
import InvestorReferralType from "../Models/Catalogue/Investments/InvestorReferralType";
import MergeTemplate from "../Models/Catalogue/General/MergeTemplate";
import LadderAge from "../Models/Catalogue/Pipeline/LadderAge";
import TableLookup from "../Models/Catalogue/Reporting/Lookups/TableLookup";
import ImportConfig from "../Models/Import/ImportConfig";
import DistributionType from "../Models/Catalogue/Classifications/DistributionType";
import { BusinessDayConvention } from "../Models/Dividends/BusinessDayConvention.enum";

export enum LifeTime {
    Short = 30,
    Long = 240
}

@injectable()
export default class DataCache extends Data.CachedDataService {

    constructor(
        private genericApiClient = AppService.get(Types.Domain.ApiClients.CatalogueGeneric),
        private generalApiClient = AppService.get(Types.Domain.ApiClients.CatalogueGeneral),
        private reportingApiClient = AppService.get(Types.Domain.ApiClients.CatalogueReporting),
        private pipelinesApiClient = AppService.get(Types.Domain.ApiClients.CataloguePipeline),
        private investmentApiClient = AppService.get(Types.Domain.ApiClients.CatalogueInvestment),
        private investmentSetupApiClient = AppService.get(Types.Domain.ApiClients.InvestmentSetup),
        private letterAuthorisationApiClient = AppService.get(Types.Domain.ApiClients.LetterAuthorisation),
        private internalApproverApiClient = AppService.get(DomainTypes.ApiClients.InternalApprover),
        private configApiClient = AppService.get(Types.Domain.ApiClients.Config),
        private importApiClient = AppService.get(Types.Domain.ApiClients.Import)
    ) {
        super();
    }

    public portfolioTypes = this.registerList(PortfolioType, this.genericApiClient.getPortfolioTypes, LifeTime.Long);
    public banks = this.registerList(Bank, this.genericApiClient.getBanks, LifeTime.Long);
    public documentTypes = this.registerList(DocumentType, this.genericApiClient.getDocumentTypes, LifeTime.Long);
    public countries = this.registerList(Country, this.genericApiClient.getCountries, LifeTime.Long);
    public legalEntityTypes = this.registerList(LegalEntityType, this.genericApiClient.getLegalEntityTypes, LifeTime.Long);
    public sectors = this.registerList(Sector, this.genericApiClient.getSectors, LifeTime.Long);
    public assetClasses = this.registerList(AssetClass, this.genericApiClient.getAssetClasses, LifeTime.Long);
    public issuerTypes = this.registerList(IssuerType, this.genericApiClient.getIssuerTypes, LifeTime.Long);
    public shareTypes = this.registerList(ShareType, this.genericApiClient.getShareTypes, LifeTime.Long);
    public bankAccountTypes = this.registerList(BankAccountType, this.pipelinesApiClient.getBankAccountTypes, LifeTime.Long);
    public titles = this.registerList(Title, this.genericApiClient.getTitles, LifeTime.Long);
    public intraGroups = this.registerList(IntraGroup, this.genericApiClient.getIntraGroups, LifeTime.Long);
    public internalRatings = this.registerList(InternalRating, this.investmentApiClient.getInternalRatings, LifeTime.Long);
    public noteTypes = this.registerList(NoteType, this.genericApiClient.getNoteTypes, LifeTime.Long);
    public entities = this.registerList(Entity, this.investmentApiClient.getEntities, LifeTime.Long);
    public assetTypes = this.registerList(AssetType, this.genericApiClient.getAssetTypes, LifeTime.Long);
    public glAccountTypes = this.registerList(GLAccountType, this.genericApiClient.getGLAccountTypes, LifeTime.Long);
    public sarsIdTypes = this.registerList(SARSIDType, this.genericApiClient.getSARSIDTypes, LifeTime.Long);
    public pipelines = this.registerList(Pipeline, this.pipelinesApiClient.getPipelines, LifeTime.Long);
    public dwtExemptions = this.registerList(DwtExemption, this.genericApiClient.getDwtExemptions, LifeTime.Long);
    public noticePeriods = this.registerList(NoticePeriod, this.investmentApiClient.getNoticePeriods, LifeTime.Long);
    public contactTypes = this.registerList(ContactType, this.genericApiClient.getContactTypes, LifeTime.Long);
    public sarsNatureOfPerson = this.registerList(SARSNatureOfPerson, this.genericApiClient.getSARSNatureOfPerson, LifeTime.Long);
    public investorReferralTypes = this.registerList(InvestorReferralType, this.investmentApiClient.getInvestorReferralTypes, LifeTime.Long);
    public productTypes = this.registerList(ProductType, this.genericApiClient.getProductTypes, LifeTime.Long);
    public ladderAges = this.registerList(LadderAge, this.pipelinesApiClient.getLadderAges, LifeTime.Long);
    public mergeTemplates = this.registerList(MergeTemplate, this.genericApiClient.getMergeTemplates, LifeTime.Long);
    public distributionTypes = this.registerList(DistributionType, this.genericApiClient.getDistributionTypes, LifeTime.Long);

    public interestRateTypes = this.registerList(InterestRateType, this.investmentSetupApiClient.getInterestRateTypes, LifeTime.Long);
    public interestRateCalculationTypes = this.registerList(InterestRateCalculationType, this.investmentSetupApiClient.getInterestRateCalculationTypes, LifeTime.Long);
    public commissionTypes = this.registerList(CommissionTypeLookup, this.pipelinesApiClient.getCommissionTypeLookups, LifeTime.Long);
    public dateCycles = this.registerList(DateCycleLookup, this.investmentSetupApiClient.getDateCycles, LifeTime.Short);
    public internalApprovalEntities = this.registerList(InternalApprovalEntity, this.internalApproverApiClient.getInternalApprovalEntities, LifeTime.Long);

    public letterAuthorisationSignatories = this.registerList(LetterAuthorisationSignatory, this.letterAuthorisationApiClient.getSignatories, LifeTime.Short);
    public tables = this.registerList(TableLookup, this.reportingApiClient.getTableLookups, LifeTime.Short);
    public importConfig = this.registerList(ImportConfig, this.importApiClient.getImportConfigs, LifeTime.Long);

    public fieldConfig!: FieldConfigValues;
    
    public tenantConfig = this.register(
        new Data.CallbackDataSource(async () => ConfigValues.fromJSObject<ConfigValues>((await this.configApiClient.getConfig()).data), 
        new ConfigValues()), LifeTime.Long);

    /** 
     * Calculates the next (or previous) business day based on the given reference date taking holidays into account.
     * This will always make a call to the server.
     */
    public calculateBusinessDayAsync(referenceDate: Date, businessDayConvention: BusinessDayConvention = BusinessDayConvention.Following, dateOffset: number = 0) {
        return this.generalApiClient.calculateBusinessDayAsync(referenceDate, businessDayConvention, dateOffset);
    }

    public async loadFieldConfig() {
        var fieldConfigResponse = await this.configApiClient.getFieldConfig();
        this.fieldConfig = FieldConfigValues.fromJSObject<FieldConfigValues>(fieldConfigResponse.data);
        this.fieldConfig.markOld();
        
        if (!this.fieldConfig) {
            throw new Error("Field config failed to load.");
        }
    }
}